import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MetricCard from '../components/MetricCard';
import { usePortal } from '../context/PortalContext';
import { clubService } from '../services/clubService';
import { ClubFinanceEntry, ClubFinancePlayerTrail } from '../types';
import { buildTimestampSuffix, downloadCsv } from '../utils/csv';
import { formatCurrency, formatDateTime, getMemberRoleLabel } from '../utils/formatters';

type TrailStatusFilter = 'ALL' | ClubFinanceEntry['status'];
type TrailDirectionFilter = 'ALL' | ClubFinanceEntry['direction'];
type TrailLimitFilter = 25 | 50 | 100;

const STATUS_OPTIONS: Array<{ value: TrailStatusFilter; label: string }> = [
  { value: 'ALL', label: 'Todos os status' },
  { value: 'OPEN', label: 'Em aberto' },
  { value: 'PARTIAL', label: 'Parcial' },
  { value: 'SETTLED', label: 'Quitado' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

const DIRECTION_OPTIONS: Array<{ value: TrailDirectionFilter; label: string }> = [
  { value: 'ALL', label: 'Todas as direcoes' },
  { value: 'RECEIVABLE', label: 'Jogador deve ao club' },
  { value: 'PAYABLE', label: 'Club deve ao jogador' },
];

const LIMIT_OPTIONS: TrailLimitFilter[] = [25, 50, 100];

const getDirectionLabel = (value: ClubFinanceEntry['direction']): string => {
  return value === 'RECEIVABLE' ? 'Jogador deve ao club' : 'Club deve ao jogador';
};

const getStatusLabel = (value: ClubFinanceEntry['status']): string => {
  switch (value) {
    case 'OPEN':
      return 'Em aberto';
    case 'PARTIAL':
      return 'Parcial';
    case 'SETTLED':
      return 'Quitado';
    case 'CANCELLED':
      return 'Cancelado';
    default:
      return value;
  }
};

const toFilterTimestamp = (value: string, time: 'start' | 'end'): string | undefined => {
  if (!value) {
    return undefined;
  }

  return time === 'start' ? `${value}T00:00:00` : `${value}T23:59:59`;
};

const toSlug = (value: string | undefined): string => {
  const normalized = (value || 'club')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || 'club';
};

const FinancePlayerTrailPage: React.FC = () => {
  const { userId = '' } = useParams();
  const { selectedClub } = usePortal();
  const [trail, setTrail] = useState<ClubFinancePlayerTrail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<TrailStatusFilter>('ALL');
  const [directionFilter, setDirectionFilter] = useState<TrailDirectionFilter>('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [limit, setLimit] = useState<TrailLimitFilter>(50);

  useEffect(() => {
    if (!selectedClub?.id || !userId) {
      setTrail(null);
      setError('');
      return;
    }

    if (fromDate && toDate && new Date(fromDate).getTime() > new Date(toDate).getTime()) {
      setTrail(null);
      setError('A data inicial nao pode ser maior que a data final');
      return;
    }

    setLoading(true);
    setError('');

    clubService
      .getFinancePlayerTrail(selectedClub.id, userId, {
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        direction: directionFilter !== 'ALL' ? directionFilter : undefined,
        from: toFilterTimestamp(fromDate, 'start'),
        to: toFilterTimestamp(toDate, 'end'),
        limit,
      })
      .then(setTrail)
      .catch((nextError: any) => {
        setTrail(null);
        setError(nextError.message || 'Erro ao carregar a trilha financeira do jogador');
      })
      .finally(() => setLoading(false));
  }, [selectedClub?.id, userId, statusFilter, directionFilter, fromDate, toDate, limit]);

  const player = trail?.player || null;
  const summary = trail?.summary || null;

  const outstandingEntries = useMemo(
    () => (trail?.entries || []).filter((entry) => entry.status === 'OPEN' || entry.status === 'PARTIAL').length,
    [trail?.entries]
  );

  const handleResetFilters = () => {
    setStatusFilter('ALL');
    setDirectionFilter('ALL');
    setFromDate('');
    setToDate('');
    setLimit(50);
  };

  const handleExportTrail = () => {
    if (!trail?.entries.length) {
      return;
    }

    const filename = `${toSlug(selectedClub?.name)}-financeiro-jogador-${toSlug(player?.user_name)}-${buildTimestampSuffix()}.csv`;
    const headers = [
      'Data',
      'Jogador',
      'Direcao',
      'Origem',
      'Valor',
      'Valor quitado',
      'Valor em aberto',
      'Status',
      'Motivo',
      'Criado por',
      'Baixas registradas',
    ];
    const rows = trail.entries.map((entry) => [
      formatDateTime(entry.created_at),
      entry.user_name,
      getDirectionLabel(entry.direction),
      entry.source_type === 'COUNTER_ADJUSTMENT' ? 'Contador' : 'Manual',
      entry.amount_reais.toFixed(2),
      entry.settled_amount_reais.toFixed(2),
      entry.outstanding_amount_reais.toFixed(2),
      getStatusLabel(entry.status),
      entry.reason || '',
      entry.created_by_name || '',
      entry.settlements
        .map(
          (settlement) =>
            `${formatDateTime(settlement.created_at)} | ${settlement.settlement_method} | ${settlement.amount_reais.toFixed(2)}`
        )
        .join(' || '),
    ]);

    downloadCsv(filename, headers, rows);
  };

  if (!selectedClub) {
    return (
      <section className="portal-section-card">
        <h3>Nenhum club selecionado</h3>
        <p className="portal-muted">Selecione um club no topo para acessar a trilha financeira.</p>
      </section>
    );
  }

  return (
    <div className="portal-page-grid">
      <section className="portal-section-card portal-section-card--hero">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Financeiro</p>
            <h3>Trilha por jogador</h3>
          </div>
          <div className="portal-section-card__header-actions">
            <Link className="portal-link-button" to="/financeiro">
              Voltar ao financeiro
            </Link>
            <button
              type="button"
              className="portal-secondary-button portal-secondary-button--compact"
              onClick={handleExportTrail}
              disabled={!trail?.entries.length}
            >
              Exportar CSV
            </button>
          </div>
        </div>

        {player ? (
          <div className="portal-detail-grid">
            <div>
              <dt>Jogador</dt>
              <dd>{player.user_name}</dd>
            </div>
            <div>
              <dt>Papel no club</dt>
              <dd>{getMemberRoleLabel(player.role)}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{player.status}</dd>
            </div>
            <div>
              <dt>Entrada no club</dt>
              <dd>{formatDateTime(player.joined_at)}</dd>
            </div>
          </div>
        ) : (
          <p className="portal-muted">Carregue um jogador valido para ver a trilha financeira.</p>
        )}
      </section>

      {error ? <div className="portal-banner portal-banner--error">{error}</div> : null}
      {loading ? <div className="portal-loading-screen">Carregando trilha financeira...</div> : null}

      <section className="portal-metrics-grid">
        <MetricCard
          label="Exposicao liquida"
          value={formatCurrency(player?.net_exposure || 0)}
          tone={(player?.net_exposure || 0) > 0 ? 'warning' : 'default'}
          helper="Receber menos pagar em aberto"
        />
        <MetricCard
          label="A receber"
          value={formatCurrency(player?.open_receivable || 0)}
          tone="positive"
          helper="Pendente para o club"
        />
        <MetricCard
          label="A pagar"
          value={formatCurrency(player?.open_payable || 0)}
          helper="Obrigacao atual do club"
        />
        <MetricCard
          label="Saldo vivo"
          value={formatCurrency(player?.live_result || 0)}
          helper="Resultado atual nas mesas"
        />
      </section>

      <section className="portal-section-card">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Resumo da trilha</p>
            <h3>Volume financeiro do jogador</h3>
          </div>
          <div className="portal-inline-stats">
            <div>
              <dt>Lancamentos</dt>
              <dd>{summary?.total_entries || 0}</dd>
            </div>
            <div>
              <dt>Pendentes</dt>
              <dd>{outstandingEntries}</dd>
            </div>
            <div>
              <dt>Mesas ativas</dt>
              <dd>{player?.active_tables || 0}</dd>
            </div>
          </div>
        </div>

        <div className="portal-detail-grid">
          <div>
            <dt>Carteira atual</dt>
            <dd>{formatCurrency(player?.wallet_balance || 0)}</dd>
          </div>
          <div>
            <dt>Ultima atividade financeira</dt>
            <dd>{formatDateTime(player?.last_financial_activity_at)}</dd>
          </div>
          <div>
            <dt>Recebiveis gerados</dt>
            <dd>{formatCurrency(summary?.receivables_created || 0)}</dd>
          </div>
          <div>
            <dt>Recebiveis baixados</dt>
            <dd>{formatCurrency(summary?.receivables_settled || 0)}</dd>
          </div>
          <div>
            <dt>Pagaveis gerados</dt>
            <dd>{formatCurrency(summary?.payables_created || 0)}</dd>
          </div>
          <div>
            <dt>Pagaveis baixados</dt>
            <dd>{formatCurrency(summary?.payables_settled || 0)}</dd>
          </div>
          <div>
            <dt>Lancamentos manuais</dt>
            <dd>{summary?.manual_entries || 0}</dd>
          </div>
          <div>
            <dt>Lancamentos do contador</dt>
            <dd>{summary?.counter_adjustment_entries || 0}</dd>
          </div>
        </div>
      </section>

      <section className="portal-section-card">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Filtros</p>
            <h3>Refinar a trilha do jogador</h3>
          </div>
          <button type="button" className="portal-secondary-button portal-secondary-button--compact" onClick={handleResetFilters}>
            Limpar filtros
          </button>
        </div>

        <div className="portal-form-grid">
          <label>
            <span>Status</span>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as TrailStatusFilter)}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Direcao</span>
            <select
              value={directionFilter}
              onChange={(event) => setDirectionFilter(event.target.value as TrailDirectionFilter)}
            >
              {DIRECTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Data inicial</span>
            <input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
          </label>

          <label>
            <span>Data final</span>
            <input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
          </label>

          <label>
            <span>Limite de linhas</span>
            <select value={limit} onChange={(event) => setLimit(Number(event.target.value) as TrailLimitFilter)}>
              {LIMIT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} registros
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="portal-section-card">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Ledger do jogador</p>
            <h3>Historico financeiro consolidado</h3>
          </div>
          <span className="portal-chip">{trail?.entries.length || 0} registros carregados</span>
        </div>

        <div className="portal-table-wrap">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Direcao</th>
                <th>Origem</th>
                <th>Valor</th>
                <th>Quitado</th>
                <th>Em aberto</th>
                <th>Status</th>
                <th>Motivo</th>
                <th>Baixas</th>
              </tr>
            </thead>
            <tbody>
              {trail?.entries.length ? (
                trail.entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDateTime(entry.created_at)}</td>
                    <td>{getDirectionLabel(entry.direction)}</td>
                    <td>{entry.source_type === 'COUNTER_ADJUSTMENT' ? 'Contador' : 'Manual'}</td>
                    <td>{formatCurrency(entry.amount_reais)}</td>
                    <td>{formatCurrency(entry.settled_amount_reais)}</td>
                    <td>{formatCurrency(entry.outstanding_amount_reais)}</td>
                    <td>{getStatusLabel(entry.status)}</td>
                    <td>{entry.reason || '-'}</td>
                    <td>
                      {entry.settlements.length ? (
                        <div className="portal-table__cell-stack">
                          {entry.settlements.map((settlement) => (
                            <span key={settlement.id}>
                              {formatDateTime(settlement.created_at)} | {settlement.settlement_method} |{' '}
                              {formatCurrency(settlement.amount_reais)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="portal-muted">Sem baixa</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9}>Nenhum lancamento encontrado para este jogador no filtro atual.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default FinancePlayerTrailPage;
